import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import { useTranslation, useI18n } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import { RecentGenerationsStrip } from '../components/RecentGenerationsStrip';
import { getFingerprintNow } from '../hooks/useFingerprint';
import { useSignupNudge } from '../components/SignupNudge';
import { usePriceQuote } from '../hooks/usePriceQuote';
import {
  AGES,
  GENDERS,
  DEFAULT_SHOT,
  SHOT_WEIGHTS,
  DEFAULT_SHOT_WEIGHT,
  PORTRAIT_FIELDS,
  PORTRAIT_GROUPS,
  RANDOMIZE,
  resolveChoice,
  localize,
  type PortraitField,
  type PortraitFieldKey,
} from '../lib/portraitOptions';

const ASPECTS = ['16:9', '4:3', '1:1', '3:4', '9:16'] as const;
type Aspect = (typeof ASPECTS)[number];
const BATCH_SIZES = [1, 2, 3, 4, 6, 8];

type Selections = Partial<Record<PortraitFieldKey, string>>;

// Polls the activity until completion, then signs every result image and lays
// them out in a grid. Human Portrait returns `batch_size` images, so we use the
// multi-key presign (same path as multi-angle) rather than single JobResult.
function HumanPortraitResult({
  activityId,
  onReset,
  resetLabel,
}: {
  activityId: number;
  onReset: () => void;
  resetLabel: string;
}) {
  const [status, setStatus] = useState<'pending' | 'processing' | 'completed' | 'failed'>('pending');
  const [urls, setUrls] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const res = await fetch(`/api/v1/history/${activityId}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`status fetch failed: ${res.status}`);
        const row = await res.json();
        if (cancelled) return;
        setStatus(row.job_status);
        const hasKeys = row.metadata?.r2_key || row.metadata?.r2_keys?.length;
        if (row.job_status === 'completed' && hasKeys) {
          const pres = await fetch(`/api/v1/history/${activityId}/presigned-url`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
          });
          if (!pres.ok) throw new Error(`presign failed: ${pres.status}`);
          const data = await pres.json();
          if (!cancelled) setUrls(Array.isArray(data.urls) ? data.urls : []);
          return;
        }
        if (row.job_status === 'failed') {
          if (!cancelled) setError(row.error_message ?? 'Generation failed');
          return;
        }
        timer = setTimeout(tick, 3_000);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
        timer = setTimeout(tick, 5_000);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activityId]);

  if (error) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>
        <Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onReset}>
            {resetLabel}
          </Button>
        </Box>
      </Stack>
    );
  }

  if (status !== 'completed' || urls.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {status === 'processing' ? 'Generating your portrait…' : 'Queued…'}
        </Typography>
        <LinearProgress sx={{ height: 8, borderRadius: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          This can take a minute, longer for larger batches.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {urls.length} image{urls.length !== 1 ? 's' : ''} generated
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: urls.length === 1 ? 'minmax(0, 420px)' : 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 1.5,
        }}
      >
        {urls.map((url, i) => (
          <Box
            key={i}
            sx={{
              position: 'relative',
              aspectRatio: '3 / 4',
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover .dl': { opacity: 1 },
            }}
          >
            <Box component="img" src={url} alt={`portrait ${i + 1}`} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <IconButton
              className="dl"
              component="a"
              href={url}
              download
              target="_blank"
              rel="noopener"
              size="small"
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                bgcolor: 'background.paper',
                opacity: 0,
                transition: 'opacity 0.2s',
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onReset}>
          {resetLabel}
        </Button>
      </Stack>
    </Stack>
  );
}

// One labelled tri-state control: Default (empty → field omitted → model
// decides), Randomize (pick one from the list), or a specific value.
function FieldControl({
  field,
  value,
  onChange,
  locale,
  defaultLabel,
  randomizeLabel,
}: {
  field: PortraitField;
  value: string;
  onChange: (v: string) => void;
  locale: string;
  defaultLabel: string;
  randomizeLabel: string;
}) {
  const label = localize(locale, field.label);
  const hint = field.hint ? localize(locale, field.hint) : undefined;
  if (field.variant === 'autocomplete') {
    const options = [RANDOMIZE, ...field.options];
    return (
      <Autocomplete
        size="small"
        options={options}
        value={value || null}
        onChange={(_, v) => onChange(v ?? '')}
        getOptionLabel={(o) => (o === RANDOMIZE ? randomizeLabel : localize(locale, o, field.key))}
        renderInput={(params) => (
          <TextField {...params} label={label} size="small" placeholder={defaultLabel} helperText={hint} />
        )}
      />
    );
  }
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      helperText={hint}
      fullWidth
    >
      <MenuItem value=""><em>{defaultLabel}</em></MenuItem>
      <MenuItem value={RANDOMIZE}>{randomizeLabel}</MenuItem>
      {field.options.map((opt) => (
        <MenuItem key={opt} value={opt}>{localize(locale, opt, field.key)}</MenuItem>
      ))}
    </TextField>
  );
}

export default function HumanPortrait() {
  const t = useTranslation();
  const { locale } = useI18n();
  const location = useLocation();
  const remix = (location.state as { remix?: Record<string, unknown> } | null)?.remix ?? null;

  const defaultLabel = t.humanPortrait.optDefault;
  const randomizeLabel = t.humanPortrait.optRandomize;

  const remixStr = (k: string): string =>
    typeof remix?.[k] === 'string' ? (remix![k] as string) : '';

  // Default state for every control is '' (Default → omit → model decides).
  const [gender, setGender] = useState<string>(remixStr('gender'));
  const [age, setAge] = useState<string>(remixStr('age'));
  const [selections, setSelections] = useState<Selections>(() => {
    // Default framing to a waist-up half-length so faces stay detailed; remix
    // overrides it when present.
    const init: Selections = { shot: DEFAULT_SHOT };
    for (const f of PORTRAIT_FIELDS) {
      const v = remixStr(f.key);
      if (v) init[f.key] = v;
    }
    return init;
  });
  const [shotWeight, setShotWeight] = useState<number>((remix?.shot_weight as number | undefined) ?? DEFAULT_SHOT_WEIGHT);
  const [promptPrefix, setPromptPrefix] = useState<string>(remixStr('prompt_prefix'));
  const [aspect, setAspect] = useState<Aspect>((remix?.aspect_ratio as Aspect | undefined) ?? '9:16');
  const [minHeight, setMinHeight] = useState<480 | 720 | 1080>((remix?.min_height as 480 | 720 | 1080 | undefined) ?? 1080);
  const [batchSize, setBatchSize] = useState<number>((remix?.batch_size as number | undefined) ?? 2);
  const [submitting, setSubmitting] = useState(false);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const perImage = usePriceQuote({ endpoint: 'human-portrait', model: 'z-image-human-base', minHeight });
  const totalCredits = perImage != null ? perImage * batchSize : null;

  const setField = (key: PortraitFieldKey) => (v: string) =>
    setSelections((prev) => {
      const next = { ...prev };
      if (v) next[key] = v;
      else delete next[key];
      return next;
    });

  const { gateGenerate } = useSignupNudge();

  const handleSubmit = async () => {
    // Anonymous users get the signup nudge instead of a doomed 402.
    if (gateGenerate()) return;
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      // Resolve every tri-state control: Default → omitted, Randomize → a
      // concrete random pick, specific → as-is. Omitted fields let the model
      // decide (no randomization) — the worker keeps the node's neutral default.
      const character: Record<string, string> = {};
      const g = resolveChoice(gender, GENDERS as unknown as string[]);
      if (g) character.gender = g;
      const a = resolveChoice(age, AGES);
      if (a) character.age = a;
      for (const f of PORTRAIT_FIELDS) {
        const v = resolveChoice(selections[f.key], f.options);
        if (v) character[f.key] = v;
      }
      // Framing strength only matters when a shot is chosen.
      if (character.shot) character.shot_weight = String(shotWeight);
      const payload: Record<string, unknown> = {
        ...character,
        aspect_ratio: aspect,
        min_height: minHeight,
        batch_size: batchSize,
        fingerprint,
      };
      if (promptPrefix.trim()) payload.prompt_prefix = promptPrefix.trim();

      const res = await fetch('/api/v1/human-portrait/generate-async', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 402) {
        const body = await res.json();
        throw new Error(`Insufficient credits (need ${body.required}, have ${body.available})`);
      }
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`submit failed: ${res.status} ${body}`);
      }
      const data = await res.json();
      setActivityId(data.activity_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setActivityId(null);
  };

  return (
    <Box>
      <PageHeader title={t.humanPortrait.title} description={t.humanPortrait.description} />
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {activityId ? (
            <Section title={t.humanPortrait.resultTitle}>
              <Paper sx={{ p: 3 }}>
                <HumanPortraitResult activityId={activityId} onReset={reset} resetLabel={t.humanPortrait.newGeneration} />
              </Paper>
            </Section>
          ) : (
            <>
              <Section title={t.humanPortrait.outputTitle}>
                <Paper sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <TextField select label={t.humanPortrait.gender} value={gender} onChange={(e) => setGender(e.target.value)} size="small" sx={{ minWidth: 140 }}>
                      <MenuItem value=""><em>{defaultLabel}</em></MenuItem>
                      <MenuItem value={RANDOMIZE}>{randomizeLabel}</MenuItem>
                      {GENDERS.map((g) => (
                        <MenuItem key={g} value={g}>{localize(locale, g)}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select label={t.humanPortrait.age} value={age} onChange={(e) => setAge(e.target.value)} size="small" sx={{ minWidth: 140 }}>
                      <MenuItem value=""><em>{defaultLabel}</em></MenuItem>
                      <MenuItem value={RANDOMIZE}>{randomizeLabel}</MenuItem>
                      {AGES.map((a) => (
                        <MenuItem key={a} value={a}>{a}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select label={t.humanPortrait.aspectRatio} value={aspect} onChange={(e) => setAspect(e.target.value as Aspect)} size="small" sx={{ minWidth: 140 }}>
                      {ASPECTS.map((a) => (
                        <MenuItem key={a} value={a}>{a}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select label={t.humanPortrait.resolution} value={minHeight} onChange={(e) => setMinHeight(Number(e.target.value) as 480 | 720 | 1080)} size="small" sx={{ minWidth: 140 }}>
                      <MenuItem value={480}>480p</MenuItem>
                      <MenuItem value={720}>720p</MenuItem>
                      <MenuItem value={1080}>1080p</MenuItem>
                    </TextField>
                    <TextField select label={t.humanPortrait.batchSize} value={batchSize} onChange={(e) => setBatchSize(Number(e.target.value))} size="small" sx={{ minWidth: 140 }}>
                      {BATCH_SIZES.map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Paper>
              </Section>

              <Section title={t.humanPortrait.characterTitle} description={t.humanPortrait.characterHint}>
                <Paper sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    {PORTRAIT_GROUPS.map((grp, gi) => {
                      const fields = PORTRAIT_FIELDS.filter((f) => f.group === grp.key);
                      if (fields.length === 0) return null;
                      return (
                        <Box key={grp.key}>
                          {gi > 0 && <Divider sx={{ mb: 2 }} />}
                          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 0.5 }}>
                            {localize(locale, grp.label)}
                          </Typography>
                          <Box
                            sx={{
                              mt: 1,
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                              gap: 2,
                            }}
                          >
                            {fields.map((f) => (
                              <FieldControl
                                key={f.key}
                                field={f}
                                value={selections[f.key] ?? ''}
                                onChange={setField(f.key)}
                                locale={locale}
                                defaultLabel={defaultLabel}
                                randomizeLabel={randomizeLabel}
                              />
                            ))}
                            {grp.key === 'framing' && (
                              <TextField
                                select
                                size="small"
                                label={localize(locale, 'Framing strength')}
                                value={shotWeight}
                                onChange={(e) => setShotWeight(Number(e.target.value))}
                                disabled={!selections.shot}
                                fullWidth
                              >
                                {SHOT_WEIGHTS.map((w) => (
                                  <MenuItem key={w.value} value={w.value}>{localize(locale, w.label)}</MenuItem>
                                ))}
                              </TextField>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Section>

              <Section title={t.humanPortrait.promptTitle} description={t.humanPortrait.promptHint}>
                <Paper sx={{ p: 3 }}>
                  <TextField
                    fullWidth
                    placeholder={t.humanPortrait.promptPlaceholder}
                    multiline
                    minRows={2}
                    value={promptPrefix}
                    onChange={(e) => setPromptPrefix(e.target.value)}
                  />
                </Paper>
              </Section>

              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

              <Stack direction="row" spacing={1.5}>
                <Button variant="contained" size="large" disabled={submitting} onClick={handleSubmit}>
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    `${t.humanPortrait.generate}${totalCredits != null ? ` (${totalCredits} ${t.credits.balanceUnit})` : ''}`
                  )}
                </Button>
              </Stack>
            </>
          )}
        </Box>
        <RecentGenerationsStrip endpoint="human-portrait" />
      </Box>
    </Box>
  );
}
